import { reactive, ref, nextTick } from "vue";
import { saveAs } from "file-saver";

import wwwconfig from "../config.json";
import { uuidv4 } from "../utils/shared.js";
import { drawLines } from "../utils/svg.js";
import { config, editDefaults } from "../utils/consts.js";
import { noWindow } from "../utils/windows.js";
import { createBackend } from "../api/backend.js";

export function useAppLogic() {
  let storyData = reactive({ data: {} });
  let uploadedData = reactive({});
  let importActions = reactive([]);

  let edit = reactive(editDefaults);
  const cardClipboard = ref(null); // { cards: [{id, data}], locs: {} }

  let session = reactive({
    user: "",
  });

  let previouslySavedData = "";

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "[::1]";

  const apiUrl = isLocalhost ? "http://localhost:3030" : wwwconfig.apiUrl;
  const backend = createBackend(apiUrl);

  function saveData() {
    console.log("SAVING");
    if (previouslySavedData == JSON.stringify(storyData.data)) {
      return;
    }
    edit.saving = true;

    backend
      .save(session.user, edit.currentProjectId, storyData.data)
      .then((data) => {
        previouslySavedData = JSON.stringify(storyData.data);
        storyData.data.extra.lastSave = new Date().toISOString();
        edit.saving = false;
        console.log(data);
      });
  }

  function downloadData() {
    const data = JSON.stringify(storyData.data, null, 4);
    const fileName = "story.json";
    const fileObj = new Blob([data], {
      type: "application/json",
    });
    saveAs(fileObj, fileName);
  }

  function uploadData() {
    const fileInput = document.getElementById("fileInput");
    fileInput.onchange = handleFileInputChange;
    fileInput.click();
  }

  function cancelImport() {
    Object.keys(uploadedData).forEach((key) => delete uploadedData[key]);
    importActions.splice(0, importActions.length);
  }

  function debugInfo() {
    console.log(storyData.data);
  }

  function linesRefresh() {
    nextTick(() => drawLines(storyData, edit));
  }

  function drawConnectLine() {
    let svgContainer = document.getElementById("connectlineSVG");
    if (!svgContainer) {
      return;
    }
    let xmlns = "http://www.w3.org/2000/svg";
    let svgElem = document.createElementNS(xmlns, "svg");
    let line = document.createElementNS(xmlns, "line");
    line.setAttributeNS(null, "stroke", "#24C3D5");
    line.setAttributeNS(null, "stroke-width", 2);
    line.setAttributeNS(null, "x1", startX);
    line.setAttributeNS(null, "y1", startY);
    line.setAttributeNS(null, "x2", pointerX);
    line.setAttributeNS(null, "y2", pointerY);

    if (connecting != "none") {
      svgElem.appendChild(line);
      svgContainer.replaceChild(svgElem, svgContainer.firstChild);
    }
  }

  let editHistory = ["none"];

  function canvasCoords(pageX, pageY) {
    const scrollPane = document.getElementById("cards");
    const rect = scrollPane.getBoundingClientRect();
    const s = edit.cardsScale;
    return {
      x: (pageX - rect.left + scrollPane.scrollLeft) / s,
      y: (pageY - rect.top + scrollPane.scrollTop) / s,
    };
  }

  function applyScale(s) {
    edit.cardsScale = Math.max(0.1, Math.min(2, parseFloat(s.toFixed(2))));
    const cards = document.getElementById("cards");
    if (cards) {
      const gs = 120 * edit.cardsScale;
      cards.style.backgroundSize = `${gs}px ${gs}px`;
      cards.style.backgroundPosition = `${-24 * edit.cardsScale}px ${-25 * edit.cardsScale}px`;
    }
    drawLines(storyData, edit);
  }

  function scaleView(scaleAmount) {
    applyScale(edit.cardsScale + scaleAmount);
  }

  function editCard(id, noPush = false) {
    if (noPush != true) {
      editHistory.push(id);
    }
    edit.editingCard = id;
    edit.canGoBack = editHistory.length > 1;
    linksFocusIndex = -1;
    paragraphsFocusIndex = -1;
  }

  function scrollToCard(cardId) {
    const container = document.getElementById("cards");
    if (!container) return;
    const card = storyData.data.stories[edit.currentStorylet].cards[cardId];
    if (!card) return;
    const s = edit.cardsScale;

    const containerRect = container.getBoundingClientRect();

    // Left occlusion: open shelf panel overlays #cards from the left
    const shelfPanel = document.querySelector('.shelf.show');
    const leftOcclusion = shelfPanel
      ? Math.max(0, shelfPanel.getBoundingClientRect().right - containerRect.left)
      : 0;

    // Right occlusion: edit card pane overlays #cards from the right
    const editPane = document.querySelector('.win-shade');
    const rightOcclusion = editPane
      ? Math.max(0, containerRect.right - editPane.getBoundingClientRect().left)
      : 0;

    const visibleWidth = Math.max(0, container.clientWidth - leftOcclusion - rightOcclusion);
    const visibleCenterX = leftOcclusion + visibleWidth / 2;

    container.scrollTo({
      left: card.editor.left * s + 59 * s - visibleCenterX,
      top: card.editor.top * s + 40 * s - container.clientHeight / 2,
      behavior: "smooth",
    });
  }

  function editPreviousCard() {
    if (editHistory.length > 1) {
      editHistory.pop();
      const prevId = editHistory[editHistory.length - 1];
      editCard(prevId, true);
      if (prevId !== "none") scrollToCard(prevId);
    }
  }

  function setStoryData(n) {
    edit.currentStorylet = "main";
    storyData.data = n;
    setTimeout(() => {
      scrollSetup();
    }, 1000);
    setTimeout(() => {
      drawLines(storyData, edit);
    }, 100);
    noWindow(edit);
  }

  function setDropTarget(target) {
    dropTarget = target;
  }

  function addTextKey() {
    let id = uuidv4();
    storyData.data.loc[id] = {};

    const keys = Object.keys(storyData.data.loc["na"]);
    keys.forEach((key, index) => {
      storyData.data.loc[id][key] = "...";
    });

    return id;
  }

  function addLink(targetCardId) {
    const linkText = addTextKey();
    const originCardId = targetCardId;
    const story = storyData.data.stories[edit.currentStorylet];

    const newLink = {
      target: targetCardId,
      linkText: linkText,
      conditionAndOr: "and",
      conditions: [],
      actions: [],
    };

    story.cards[originCardId].links.push(newLink);
  }

  function nodeOutDown(args) {
    console.log("Connecting node: ", args);
    let id = args[0];
    let index = args[1];
    let linkindex = args[2];
    connectSourceCard = index;
    connectLinkIndex = linkindex;
    let el = document.getElementById(id);
    let elcoords = el.getBoundingClientRect();
    startX = elcoords.left + 5;
    startY = elcoords.top + 5;
    pointerX = startX;
    pointerY = startY;
    connecting = index;

    let svgContainer = document.getElementById("connectlineSVG");
    svgContainer.style.display = "block";
  }

  let connectSourceCard = "";
  let connectLinkIndex = "";
  let dragging = "none";
  let draggingCardAdd = "none";
  let connecting = "none";
  let dropTarget = "none";
  let startX = -1;
  let startY = -1;
  let pointerX = -1;
  let pointerY = -1;
  let downtime = 0;

  let offsetX = -1;
  let offsetY = -1;

  let controlDown = false;
  let shiftDown = false;

  let isDraggingSelection = false;
  let selectionDragStartPointer = { x: 0, y: 0 };
  let selectionDragStartPositions = {};

  function draggingCard(cardType) {
    draggingCardAdd = cardType;
    edit.draggingCardAdd = cardType;
  }

  document.onkeydown = function (e) {
    if (e.code == "ControlLeft") {
      controlDown = true;
    }

    if (e.code == "ShiftLeft") {
      shiftDown = true;
    }
  };

  document.onkeyup = function (e) {
    if (e.code == "ControlLeft") {
      controlDown = false;
    }

    if (e.code == "ShiftLeft") {
      shiftDown = false;
    }
  };

  document.onmouseup = function (event) {
    if (draggingCardAdd == "card") {
      addCard();
    }
    if (draggingCardAdd == "logic") {
      addLogic();
    }

    if (draggingCardAdd == "operator") {
      addOperator();
    }

    if (draggingCardAdd == "meta") {
      addMeta();
    }

    if (draggingCardAdd == "detour") {
      addDetour();
    }

    if (draggingCardAdd == "note") {
      addNote();
    }

    if (controlDown) {
      let id = addCard();
      setDropTarget(id);
      cardUp();
    }

    cardUp();

    draggingCardAdd = "none";
    edit.draggingCardAdd = "none";

    let svgContainer = document.getElementById("connectlineSVG");
    if (svgContainer) {
      svgContainer.style.display = "none";
    }

    if (storyData.data.stories) {
      drawLines(storyData, edit);
    }
  };

  function cardDown(cardId) {
    if (edit.selectedCards.includes(cardId)) {
      isDraggingSelection = true;
      selectionDragStartPointer = { x: pointerX, y: pointerY };
      const cards = storyData.data.stories[edit.currentStorylet].cards;
      selectionDragStartPositions = {};
      for (const id of edit.selectedCards) {
        selectionDragStartPositions[id] = {
          left: cards[id].editor.left,
          top: cards[id].editor.top,
        };
      }
    } else {
      edit.selectedCards = [];
      dragging = cardId;
      const pos = canvasCoords(pointerX, pointerY);
      const card = storyData.data.stories[edit.currentStorylet].cards[dragging];
      offsetX = pos.x - card.editor.left;
      offsetY = pos.y - card.editor.top;
    }
  }

  document.onmousemove = function (event) {
    pointerX = event.pageX;
    pointerY = event.pageY;
    const swatch = document.getElementById("cardAddSwatch");
    if (swatch) {
      swatch.style.left = pointerX + "px";
      swatch.style.top = pointerY + "px";
    }
  };

  function cardDropX() {
    return canvasCoords(pointerX, pointerY).x;
  }

  function cardDropY() {
    return canvasCoords(pointerX, pointerY).y;
  }

  function setCurrentStory(storyName) {
    edit.currentStorylet = storyName;
    drawLines(storyData, edit);
    noWindow(edit);
  }

  function setLanguage(lang) {
    storyData.data.currentLanguage = lang;
  }

  function deleteLanguage(lang) {
    setLanguage("en");
    const keys = Object.keys(storyData.data.loc);
    keys.forEach((key, index) => {
      delete storyData.data.loc[key][lang];
    });
  }

  function addLanguage() {
    let language = uuidv4();
    const keys = Object.keys(storyData.data.loc);
    keys.forEach((key, index) => {
      storyData.data.loc[key][language] = "";
    });
  }

  function over(key) {
    edit.over = key;
  }

  function newCardReposition(id) {
    storyData.data.stories[edit.currentStorylet].cards[id].editor.left =
      cardDropX();
    storyData.data.stories[edit.currentStorylet].cards[id].editor.top =
      cardDropY();
  }

  function addCard(fromEdit = false) {
    if (edit.over != "cards") {
      return;
    }

    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newCard)
    );

    newCardReposition(id);

    return id;
  }

  function addDetour() {
    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newDetour)
    );

    newCardReposition(id);
  }

  function addLogic() {
    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newLogic)
    );

    newCardReposition(id);
  }

  function addOperator() {
    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newOperator)
    );

    newCardReposition(id);
  }

  function addMeta() {
    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newMeta)
    );

    newCardReposition(id);
  }

  function addNote() {
    let id = uuidv4();
    storyData.data.stories[edit.currentStorylet].cards[id] = JSON.parse(
      JSON.stringify(config.newNote)
    );

    newCardReposition(id);
  }

  function deleteCard(id) {
    const card = storyData.data.stories[edit.currentStorylet].cards[id];

    dragging = "none";

    // Clean up paragraph texts
    if (card.paragraphs) {
      card.paragraphs?.forEach((paragraph) => {
        delete storyData.data.loc[paragraph.text];
      });
    }
    // Clean up link texts
    if (card.links) {
      card.links?.forEach((link) => {
        if (link.linkText != "na") {
          delete storyData.data.loc[link.linkText];
        }
      });
    }

    // Clear all links pointing to this card
    Object.values(storyData.data.stories[edit.currentStorylet].cards).forEach(
      (cardData) => {
        cardData.links?.forEach((link) => {
          if (link.target === id) {
            link.target = "";
          }
        });
      }
    );

    // Close the editor if this card was open
    if (edit.editingCard === id) {
      edit.editingCard = "none";
    }

    // Delete the card
    delete storyData.data.stories[edit.currentStorylet].cards[id];
    drawLines(storyData, edit);
  }

  function deleteSelectedCards() {
    [...edit.selectedCards].forEach((id) => deleteCard(id));
    edit.selectedCards = [];
  }

  function copySelectedCards() {
    if (!edit.selectedCards.length) return;
    const cards = edit.selectedCards.map((id) => ({
      id,
      data: JSON.parse(JSON.stringify(
        storyData.data.stories[edit.currentStorylet].cards[id]
      )),
    }));
    const locs = {};
    cards.forEach(({ data }) => {
      data.paragraphs?.forEach((p) => {
        if (storyData.data.loc[p.text])
          locs[p.text] = JSON.parse(JSON.stringify(storyData.data.loc[p.text]));
      });
      data.links?.forEach((l) => {
        if (l.linkText && l.linkText !== "na" && storyData.data.loc[l.linkText])
          locs[l.linkText] = JSON.parse(JSON.stringify(storyData.data.loc[l.linkText]));
      });
    });
    cardClipboard.value = { cards, locs };
  }

  function cutSelectedCards() {
    copySelectedCards();
    [...edit.selectedCards].forEach((id) => deleteCard(id));
    edit.selectedCards = [];
  }

  function pasteCards() {
    if (!cardClipboard.value) return;
    const { cards, locs } = cardClipboard.value;
    const idMap = {};
    cards.forEach(({ id }) => { idMap[id] = uuidv4(); });
    const newIds = [];
    cards.forEach(({ id: oldId, data }) => {
      const newCard = JSON.parse(JSON.stringify(data));
      // Remap paragraph loc keys
      newCard.paragraphs?.forEach((p) => {
        const newKey = uuidv4();
        storyData.data.loc[newKey] = locs[p.text]
          ? JSON.parse(JSON.stringify(locs[p.text]))
          : Object.fromEntries(Object.keys(storyData.data.loc.na).map((lang) => [lang, "..."]));
        p.text = newKey;
      });
      // Remap link loc keys and link targets
      const destCards = storyData.data.stories[edit.currentStorylet].cards;
      newCard.links?.forEach((l) => {
        if (l.linkText && l.linkText !== "na") {
          const newKey = uuidv4();
          storyData.data.loc[newKey] = locs[l.linkText]
            ? JSON.parse(JSON.stringify(locs[l.linkText]))
            : Object.fromEntries(Object.keys(storyData.data.loc.na).map((lang) => [lang, "..."]));
          l.linkText = newKey;
        }
        if (l.target && idMap[l.target]) {
          l.target = idMap[l.target];
        } else if (l.target && !destCards[l.target]) {
          l.target = "";
        }
      });
      // Clear afterDetour if it doesn't resolve in the destination storylet
      if (newCard.afterDetour !== undefined) {
        if (!idMap[newCard.afterDetour] && !destCards[newCard.afterDetour]) {
          newCard.afterDetour = "";
        } else if (idMap[newCard.afterDetour]) {
          newCard.afterDetour = idMap[newCard.afterDetour];
        }
      }
      newCard.editor.left += 30;
      newCard.editor.top += 30;
      storyData.data.stories[edit.currentStorylet].cards[idMap[oldId]] = newCard;
      newIds.push(idMap[oldId]);
    });
    edit.selectedCards = newIds;
    drawLines(storyData, edit);
  }

  let gridSize = 24;
  let snapCorrectX = 0;
  let snapCorrectY = 0;

  function cardUp() {
    if (downtime <= 10 && dragging != "none") {
      // Card click logic (commented out in original)
    }

    if (dragging != "none") {
      storyData.data.stories[edit.currentStorylet].cards[dragging].editor.left =
        edit.snapX + snapCorrectX;
      storyData.data.stories[edit.currentStorylet].cards[dragging].editor.top =
        edit.snapY + snapCorrectY;
    }

    if (isDraggingSelection) {
      isDraggingSelection = false;
      selectionDragStartPositions = {};
    }

    dragging = "none";
    if (connecting != "none") {
      storyData.data.stories[edit.currentStorylet].cards[connectSourceCard].links[
        connectLinkIndex
      ].target = dropTarget;
      if (
        storyData.data.stories[edit.currentStorylet].cards[dropTarget] &&
        storyData.data.stories[edit.currentStorylet].cards[dropTarget].nodeType ==
        "detour"
      ) {
        storyData.data.stories[edit.currentStorylet].cards[
          dropTarget
        ].afterDetour = connectSourceCard;
      }
      connecting = "none";
      let svgContainer = document.getElementById("connectlineSVG");
      svgContainer.style.display = "none";
    }
    downtime = 0;

    drawLines(storyData, edit);
  }

  function autoSave() {
    let delay = 30000;
    if (storyData.data.settings) {
      delay = storyData.data.settings.saveInterval * 1000;
      saveData();
    }

    setTimeout(autoSave, delay);
  }

  autoSave();

  setInterval(() => {
    if (isDraggingSelection) {
      downtime += 1;
      const cards = storyData.data.stories[edit.currentStorylet].cards;
      const s = edit.cardsScale;
      const deltaX = (pointerX - selectionDragStartPointer.x) / s;
      const deltaY = (pointerY - selectionDragStartPointer.y) / s;
      const snapEnabled = storyData.data.settings?.snapGrid !== false;
      for (const id of edit.selectedCards) {
        let newLeft = selectionDragStartPositions[id].left + deltaX;
        let newTop = selectionDragStartPositions[id].top + deltaY;
        if (snapEnabled) {
          newLeft = Math.floor(newLeft / gridSize) * gridSize;
          newTop = Math.floor(newTop / gridSize) * gridSize;
        }
        cards[id].editor.left = newLeft;
        cards[id].editor.top = newTop;
      }
      drawLines(storyData, edit);
    }

    if (dragging != "none") {
      downtime += 1;
      const pos = canvasCoords(pointerX, pointerY);
      const card = storyData.data.stories[edit.currentStorylet].cards[dragging];
      const snapEnabled = storyData.data.settings?.snapGrid !== false;
      const rawX = pos.x - offsetX;
      const rawY = pos.y - offsetY;

      edit.snapX = snapEnabled ? Math.floor(rawX / gridSize) * gridSize : rawX;
      edit.snapY = snapEnabled ? Math.floor(rawY / gridSize) * gridSize : rawY;

      card.editor.left = edit.snapX;
      card.editor.top = edit.snapY;

      const snapGuide = document.getElementById("snapGuide");
      if (snapGuide) {
        snapGuide.style.left = edit.snapX + "px";
        snapGuide.style.top = edit.snapY + "px";
      }

      drawLines(storyData, edit);
    }

    drawConnectLine();
  }, 20);

  const loginWithToken = async (token) => {
    if (isLocalhost) {
      session.user = "local";
      return;
    }

    const data = await backend.account(token);
    console.log(data.status);

    if (data.status == 2) {
      session.user = token;
    }
  };

  window.onload = function () {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "[::1]";

    if (wwwconfig.standalone) {
      session.user = "local";
      return;
    }

    const token = getToken();
    console.log("Token: ", token);

    if (token) {
      loginWithToken(token);
    }
  };

  function getToken() {
    const urlParams = new URLSearchParams(window.location.search);
    let token = localStorage.getItem("token") || urlParams.get("token");

    if (!token && isLocalhost) {
      token = "local";
    }

    return token;
  }

  let overElement = "";

  function scrollSetup() {
    const scrollable = document.getElementById("cards");
    console.log(scrollable);

    let isDragging = false;
    let isSelecting = false;
    let selectStartX, selectStartY;
    let startX, startY, scrollLeft, scrollTop;

    scrollable.addEventListener("mousedown", (e) => {
      if ((e.button === 1 || (e.button === 0 && e.shiftKey)) && overElement == "svglines") {
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;
        scrollLeft = scrollable.scrollLeft;
        scrollTop = scrollable.scrollTop;
        scrollable.style.cursor = "grabbing";
        e.preventDefault();
      } else if (
        e.button === 0 &&
        overElement == "svglines" &&
        draggingCardAdd == "none" &&
        connecting == "none"
      ) {
        isSelecting = true;
        const rect = scrollable.getBoundingClientRect();
        const s = edit.cardsScale;
        selectStartX = (e.clientX - rect.left + scrollable.scrollLeft) / s;
        selectStartY = (e.clientY - rect.top + scrollable.scrollTop) / s;
        edit.selectionBox = { active: true, x: selectStartX, y: selectStartY, w: 0, h: 0 };
        edit.selectedCards = [];
        scrollable.style.cursor = "crosshair";
        e.preventDefault();
      }
    });

    scrollable.addEventListener("mousemove", (e) => {
      if (isDragging) {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        scrollable.scrollLeft = scrollLeft - deltaX;
        scrollable.scrollTop = scrollTop - deltaY;
        return;
      }

      if (isSelecting) {
        const rect = scrollable.getBoundingClientRect();
        const s = edit.cardsScale;
        const currentX = (e.clientX - rect.left + scrollable.scrollLeft) / s;
        const currentY = (e.clientY - rect.top + scrollable.scrollTop) / s;
        edit.selectionBox = {
          active: true,
          x: Math.min(selectStartX, currentX),
          y: Math.min(selectStartY, currentY),
          w: Math.abs(currentX - selectStartX),
          h: Math.abs(currentY - selectStartY),
        };
      }
    });

    document.addEventListener("mousemove", function (e) {
      const element = document.elementFromPoint(e.clientX, e.clientY);

      if (element) {
        overElement = element.id;
      }
    });

    function finishSelecting() {
      isSelecting = false;
      scrollable.style.cursor = "pointer";
      if (storyData.data.stories && edit.currentStorylet !== null) {
        const box = edit.selectionBox;
        if (box.w > 5 || box.h > 5) {
          const cards = storyData.data.stories[edit.currentStorylet].cards;
          const selected = [];
          for (const [id, card] of Object.entries(cards)) {
            const cardLeft = card.editor.left;
            const cardTop = card.editor.top;
            const cardRight = cardLeft + 118;
            const cardBottom = cardTop + 80;
            if (
              cardLeft < box.x + box.w &&
              cardRight > box.x &&
              cardTop < box.y + box.h &&
              cardBottom > box.y
            ) {
              selected.push(id);
            }
          }
          edit.selectedCards = selected;
        }
      }
      edit.selectionBox.active = false;
    }

    scrollable.addEventListener("mouseup", (e) => {
      if (e.button === 1 && isDragging) {
        isDragging = false;
        scrollable.style.cursor = "pointer";
        e.preventDefault();
      } else if (e.button === 0) {
        if (isDragging) {
          isDragging = false;
          scrollable.style.cursor = "pointer";
        }
        if (isSelecting) {
          finishSelecting();
        }
        e.preventDefault();
      }
    });

    scrollable.addEventListener("mouseleave", () => {
      if (isDragging) {
        isDragging = false;
        scrollable.style.cursor = "pointer";
      }
      if (isSelecting) {
        finishSelecting();
      }
    });

    scrollable.addEventListener("wheel", (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.1 : -0.1;
      applyScale(edit.cardsScale + delta);
    }, { passive: false });
  }

  let linksFocusIndex = 0;
  let paragraphsFocusIndex = 0;

  function focusParagraph(index) {
    console.log(index);
    paragraphsFocusIndex = index;
  }

  function followLink() {
    const card =
      storyData.data.stories[edit.currentStorylet].cards[edit.editingCard];

    if (
      edit.editingCard == "none" ||
      !card.links[linksFocusIndex] ||
      card.links.length == 0 ||
      card.links[0].target == edit.editingCard
    ) {
      return;
    }
    editCard(card.links[linksFocusIndex].target);

    let radioButton = document.getElementById("tab1");
    if (radioButton) {
      radioButton.checked = true;
    }
  }

  document.addEventListener("keydown", function (event) {
    if (event.key === "s" && controlDown) {
      saveData();
      event.preventDefault();
    }

    const inTextField = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);

    if (!inTextField) {
      if (event.key === "c" && controlDown && edit.selectedCards.length > 0) {
        copySelectedCards();
        event.preventDefault();
      }
      if (event.key === "x" && controlDown && edit.selectedCards.length > 0) {
        cutSelectedCards();
        event.preventDefault();
      }
      if (event.key === "v" && controlDown && cardClipboard.value) {
        pasteCards();
        event.preventDefault();
      }
      if ((event.key === "Delete" || event.key === "Backspace") && edit.selectedCards.length > 0) {
        deleteSelectedCards();
        event.preventDefault();
      }
    }

    if (edit.editingCard != "none") {
      if (event.key === "Escape") {
        edit.editingCard = "none";
        event.preventDefault();
      }

      if (event.key === "," && controlDown) {
        editPreviousCard();
        event.preventDefault();
      }

      if (event.key === "." && controlDown) {
        followLink();
        event.preventDefault();
      }

      if (event.key === "ArrowDown" && controlDown) {
        const linkTextareas = document.querySelectorAll(`input.link-input`);
        if (linkTextareas.length > 0) {
          linksFocusIndex = linksFocusIndex + 1;
          if (linksFocusIndex >= linkTextareas.length) linksFocusIndex = 0;
          linkTextareas[linksFocusIndex].focus();
          event.preventDefault();
        } else {
          const paragraphTextareas = document.querySelectorAll(`textarea`);
          if (paragraphTextareas.length > 0) {
            paragraphsFocusIndex = paragraphsFocusIndex + 1;
            if (paragraphsFocusIndex >= paragraphTextareas.length)
              paragraphsFocusIndex = 0;
            paragraphTextareas[paragraphsFocusIndex].focus();
            event.preventDefault();
          }
        }
      }

      if (event.key === "ArrowUp" && controlDown) {
        const linkTextareas = document.querySelectorAll(`input.link-input`);
        if (linkTextareas.length > 0) {
          linksFocusIndex = linksFocusIndex - 1;
          if (linksFocusIndex < 0) linksFocusIndex = linkTextareas.length - 1;
          linkTextareas[linksFocusIndex].focus();
          event.preventDefault();
        } else {
          const paragraphTextareas = document.querySelectorAll(`textarea`);
          if (paragraphTextareas.length > 0) {
            paragraphsFocusIndex = paragraphsFocusIndex - 1;
            if (paragraphsFocusIndex < 0)
              paragraphsFocusIndex = paragraphTextareas.length - 1;
            paragraphTextareas[paragraphsFocusIndex].focus();
            event.preventDefault();
          }
        }
      }
    }
  });

  function handleFileInputChange(event) {
    const file = event.target.files[0];

    if (!file || file.type !== "application/json") {
      alert("Please select a valid JSON file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonContent = JSON.parse(e.target.result);
        processJsonContent(jsonContent);
      } catch (err) {
        console.error("Error parsing JSON:", err);
      }
    };

    reader.readAsText(file);
  }

  function processJsonContent(jsonContent) {
    if (!jsonContent.stories) {
      console.error("Not a valid story file");
      return;
    }

    Object.assign(uploadedData, JSON.parse(JSON.stringify(jsonContent)));
    const newActions = Object.keys(storyData.data.stories).map((key) => ({
      destinationStoryKey: key,
      action: "nothing",
      uploadedStoryKey: "",
    }));
    importActions.splice(0, importActions.length, ...newActions);
  }

  return {
    storyData,
    uploadedData,
    importActions,
    edit,
    session,
    apiUrl,
    backend,
    dragging,
    saveData,
    downloadData,
    uploadData,
    cancelImport,
    debugInfo,
    linesRefresh,
    scaleView,
    editCard,
    scrollToCard,
    editPreviousCard,
    cardClipboard,
    deleteSelectedCards,
    copySelectedCards,
    cutSelectedCards,
    pasteCards,
    setStoryData,
    setDropTarget,
    addTextKey,
    addLink,
    nodeOutDown,
    draggingCard,
    cardDown,
    cardUp,
    setCurrentStory,
    setLanguage,
    deleteLanguage,
    addLanguage,
    over,
    focusParagraph,
    deleteCard,
  };
}
